import { useEffect, useMemo, useRef, useState } from 'react'
import type { DeepStats, MatchData, PlayerStat } from '../types'
import { Scoreboard } from './Scoreboard'
import { RoundTimeline, SectionLabel } from './RoundTimeline'
import { transformFor } from '../data/radar'

const SUBTABS = ['Scoreboard', 'Weapons', 'Duels & clutches', 'Utility', 'Economy', 'Timeouts', 'Heatmap'] as const
type Sub = (typeof SUBTABS)[number]

export function MatchStats({
  match,
  ourTeam,
  deepUrl = '/deepstats.json',
}: {
  match: MatchData
  ourTeam: string
  deepUrl?: string
}) {
  const [sub, setSub] = useState<Sub>('Scoreboard')
  const [deep, setDeep] = useState<DeepStats | null>(null)
  useEffect(() => {
    setDeep(null)
    fetch(deepUrl)
      .then((r) => r.json())
      .then(setDeep)
      .catch(() => setDeep(null))
  }, [deepUrl])

  // players grouped by team in scoreboard (rating) order
  const rows = match.scoreboard
  const teams = [...new Set(rows.map((r) => r.team))].sort((a) => (a === ourTeam ? -1 : 1))

  return (
    <>
      <RoundTimeline rounds={match.round_timeline} ourTeam={ourTeam} timeouts={deep?.timeouts?.events} />
      <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
        <nav className="mb-4 flex flex-wrap gap-1.5">
          {SUBTABS.map((t) => (
            <button
              key={t}
              onClick={() => setSub(t)}
              className="px-3 py-1 text-xs font-medium"
              style={{
                borderRadius: 'var(--radius)',
                color: sub === t ? 'var(--accent-ink)' : 'var(--text-dim)',
                background: sub === t ? 'var(--accent)' : 'transparent',
                border: '1px solid var(--border)',
              }}
            >
              {t}
            </button>
          ))}
        </nav>

        {sub === 'Scoreboard' ? (
          <Scoreboard match={match} ourTeam={ourTeam} />
        ) : !deep ? (
          <div className="text-sm text-text-dim">Loading…</div>
        ) : sub === 'Weapons' ? (
          <Weapons deep={deep} rows={rows} teams={teams} ourTeam={ourTeam} />
        ) : sub === 'Duels & clutches' ? (
          <Duels deep={deep} rows={rows} teams={teams} ourTeam={ourTeam} />
        ) : sub === 'Utility' ? (
          <Utility deep={deep} rows={rows} teams={teams} ourTeam={ourTeam} />
        ) : sub === 'Economy' ? (
          <Economy deep={deep} ourTeam={ourTeam} />
        ) : sub === 'Timeouts' ? (
          <Timeouts deep={deep} ourTeam={ourTeam} />
        ) : (
          <Heatmap deep={deep} ourTeam={ourTeam} rounds={match.round_timeline} />
        )}
      </section>
    </>
  )
}

// ---------- shared ----------
type ViewProps = { deep: DeepStats; rows: PlayerStat[]; teams: string[]; ourTeam: string }

function TeamGroups({
  rows,
  teams,
  ourTeam,
  head,
  row,
}: {
  rows: PlayerStat[]
  teams: string[]
  ourTeam: string
  head: React.ReactNode
  row: (p: PlayerStat) => React.ReactNode
}) {
  return (
    <div className="space-y-5">
      {teams.map((team) => (
        <div key={team}>
          <div className={`mb-2 font-display text-sm font-semibold ${team === ourTeam ? 'text-accent' : 'text-text'}`}>
            {team}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-140 border-collapse text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-faint">{head}</tr>
              </thead>
              <tbody>
                {rows.filter((p) => p.team === team).map((p) => (
                  <tr key={p.steamid64} className="border-t border-border/60 hover:bg-surface-2">
                    {row(p)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

const Th = ({ children, l }: { children: React.ReactNode; l?: boolean }) => (
  <th className={`px-2 py-1.5 font-medium ${l ? 'text-left' : 'text-right'}`}>{children}</th>
)
const Td = ({ children, l, dim }: { children: React.ReactNode; l?: boolean; dim?: boolean }) => (
  <td className={`px-2 py-2 tabular-nums ${l ? 'text-left font-medium text-text' : 'text-right'} ${dim ? 'text-text-dim' : 'text-text'}`}>
    {children}
  </td>
)

const WEP: Record<string, string> = {
  ak47: 'AK-47', m4a1: 'M4A4', m4a1_silencer: 'M4A1-S', awp: 'AWP', ssg08: 'Scout',
  usp_silencer: 'USP-S', glock: 'Glock', hkp2000: 'P2000', deagle: 'Deagle', fiveseven: 'Five-Seven',
  galilar: 'Galil', famas: 'FAMAS', mp9: 'MP9', mac10: 'MAC-10', hegrenade: 'HE', p250: 'P250',
  tec9: 'Tec-9', sg556: 'SG 553', aug: 'AUG', ump45: 'UMP', xm1014: 'XM1014', nova: 'Nova',
}
const wep = (w: string) => WEP[w] ?? w.replace(/_/g, ' ')

function topWeapons(byWeapon: Record<string, { kills: number; hs: number }>, n = 3) {
  return Object.entries(byWeapon)
    .sort((a, b) => b[1].kills - a[1].kills)
    .slice(0, n)
    .map(([w, v]) => `${wep(w)} ${v.kills}`)
    .join(' · ')
}

// ---------- Weapons ----------
function Weapons({ deep, rows, teams, ourTeam }: ViewProps) {
  return (
    <>
      <SectionLabel n="W" label="Weapons & accuracy" />
      <div className="mt-4">
        <TeamGroups
          rows={rows}
          teams={teams}
          ourTeam={ourTeam}
          head={
            <>
              <Th l>Player</Th>
              <Th>Shots</Th>
              <Th>Hits</Th>
              <Th>Acc%</Th>
              <Th>HS%</Th>
              <th className="px-2 py-1.5 text-right font-medium">Top weapons</th>
            </>
          }
          row={(p) => {
            const w = deep.weapons[p.steamid64]
            return (
              <>
                <Td l>{p.name}</Td>
                <Td dim>{w?.shots ?? 0}</Td>
                <Td dim>{w?.hits ?? 0}</Td>
                <Td>{w ? w.acc : 0}</Td>
                <Td dim>{p.hs_pct}</Td>
                <td className="px-2 py-2 text-right text-[11px] text-text-dim">
                  {w ? topWeapons(w.byWeapon) : '–'}
                </td>
              </>
            )
          }}
        />
      </div>
    </>
  )
}

// ---------- Duels & clutches ----------
function Duels({ deep, rows, teams, ourTeam }: ViewProps) {
  const clutchCols = ['1v1', '1v2', '1v3', '1v4', '1v5']
  return (
    <>
      <SectionLabel n="D" label="Opening duels & clutches" />
      <div className="mt-4">
        <TeamGroups
          rows={rows}
          teams={teams}
          ourTeam={ourTeam}
          head={
            <>
              <Th l>Player</Th>
              <Th>Open CT</Th>
              <Th>Open T</Th>
              {clutchCols.map((c) => (
                <Th key={c}>{c}</Th>
              ))}
              <Th>Clutch</Th>
            </>
          }
          row={(p) => {
            const o = deep.opening[p.steamid64]
            const c = deep.clutches[p.steamid64] ?? {}
            const totW = Object.values(c).reduce((s, v) => s + v.w, 0)
            const totP = Object.values(c).reduce((s, v) => s + v.p, 0)
            return (
              <>
                <Td l>{p.name}</Td>
                <Td dim>{o ? `${o.CT.k}-${o.CT.d}` : '0-0'}</Td>
                <Td dim>{o ? `${o.T.k}-${o.T.d}` : '0-0'}</Td>
                {clutchCols.map((col) => (
                  <Td key={col} dim={!c[col]}>
                    {c[col] ? `${c[col].w}/${c[col].p}` : '–'}
                  </Td>
                ))}
                <Td>
                  <span style={{ color: totW ? 'var(--accent)' : 'var(--text-dim)' }}>
                    {totW}/{totP}
                  </span>
                </Td>
              </>
            )
          }}
        />
      </div>
    </>
  )
}

// ---------- Utility ----------
function Utility({ deep, rows, teams, ourTeam }: ViewProps) {
  return (
    <>
      <SectionLabel n="U" label="Utility" />
      <div className="mt-4">
        <TeamGroups
          rows={rows}
          teams={teams}
          ourTeam={ourTeam}
          head={
            <>
              <Th l>Player</Th>
              <Th>Flashes</Th>
              <Th>Enemies blinded</Th>
              <Th>Blind s</Th>
              <Th>FA</Th>
              <Th>Team-flash</Th>
              <Th>HE dmg</Th>
              <Th>Fire dmg</Th>
            </>
          }
          row={(p) => {
            const u = deep.utility[p.steamid64]
            return (
              <>
                <Td l>{p.name}</Td>
                <Td dim>{u?.flashes ?? 0}</Td>
                <Td>{u?.enemiesFlashed ?? 0}</Td>
                <Td dim>{u ? u.blindTime.toFixed(0) : 0}</Td>
                <Td>{u?.flashAssists ?? 0}</Td>
                <td className="px-2 py-2 text-right tabular-nums" style={{ color: (u?.teamFlashed ?? 0) > 15 ? 'var(--loss)' : 'var(--text-dim)' }}>
                  {u?.teamFlashed ?? 0}
                </td>
                <Td dim>{u?.heDmg ?? 0}</Td>
                <Td dim>{u?.fireDmg ?? 0}</Td>
              </>
            )
          }}
        />
      </div>
    </>
  )
}

// ---------- Economy ----------
const ETYPES = ['PISTOL', 'ECO', 'FORCE', 'FULL']
function Economy({ deep, ourTeam }: { deep: DeepStats; ourTeam: string }) {
  const pct = (v: { w: number; p: number }) => (v.p ? Math.round((100 * v.w) / v.p) : 0)
  return (
    <>
      <SectionLabel n="E" label="Economy" />
      <p className="mt-2 text-[11px] text-text-faint">
        Win rate by round type. Raw buy values are approximate (equip sampled near buy time).
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-105 border-collapse text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-text-faint">
              <Th l>Team</Th>
              {ETYPES.map((t) => (
                <Th key={t}>{t}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deep.teams.map((team) => (
              <tr key={team} className="border-t border-border/60">
                <Td l>
                  <span className={team === ourTeam ? 'text-accent' : ''}>{team}</span>
                </Td>
                {ETYPES.map((t) => {
                  const v = deep.economy.byType[team]?.[t]
                  return (
                    <Td key={t} dim={!v}>
                      {v ? `${pct(v)}% (${v.w}/${v.p})` : '–'}
                    </Td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-text-faint">
          {ourTeam} round economy
        </div>
        <div className="flex flex-wrap gap-1">
          {deep.economy.rounds.map((r) => {
            const won = r.winner === ourTeam
            const type = r.types[ourTeam]
            return (
              <div
                key={r.round}
                title={`R${r.round} · ${type} · ${won ? 'won' : 'lost'}`}
                className="flex h-8 w-8 flex-col items-center justify-center text-[9px]"
                style={{
                  borderRadius: 'var(--radius)',
                  background: won ? 'var(--accent)' : 'var(--loss)',
                  color: won ? 'var(--accent-ink)' : '#fff',
                }}
              >
                <span className="font-display">{r.round}</span>
                <span className="opacity-80">{type[0]}</span>
              </div>
            )
          })}
        </div>
        <div className="mt-2 text-[11px] text-text-faint">P pistol · E eco · F force · full = full-buy</div>
      </div>
    </>
  )
}

// ---------- Timeouts (coach view: does calling one break momentum?) ----------
function Timeouts({ deep, ourTeam }: { deep: DeepStats; ourTeam: string }) {
  const t = deep.timeouts
  if (!t || !t.events.length) {
    return (
      <p className="text-sm text-text-faint">
        No tactical timeouts detected in this match.
        {t ? ` (normal break ${t.baselineGapSec}s; a timeout adds ${t.timeoutSec}s.)` : ' Re-parse the demo to compute them.'}
      </p>
    )
  }
  const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0)
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        {Object.entries(t.byTeam).map(([team, s]) => (
          <div key={team} className="border border-border bg-surface-2 p-4" style={{ borderRadius: 'var(--radius)' }}>
            <div className="font-display text-sm uppercase tracking-wider" style={{ color: team === ourTeam ? 'var(--accent)' : 'var(--text)' }}>
              {team}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <Stat label="Next round" value={`${s.afterTimeoutWon}/${s.afterTimeoutPlayed}`} sub={`${pct(s.afterTimeoutWon, s.afterTimeoutPlayed)}%`} />
              <Stat label="Next 3 rounds" value={`${s.next3Won}/${s.next3Played}`} sub={`${pct(s.next3Won, s.next3Played)}%`} />
              <Stat label="Broke streak" value={`${s.brokeStreak}/${s.called}`} sub={s.called ? `of their ${s.called}` : '—'} />
            </div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-text-faint">
              <th className="py-1 pr-3">Before round</th>
              <th className="px-2">Break</th>
              <th className="px-2">Momentum going in</th>
              <th className="px-2">Called by</th>
              <th className="px-2">Next round</th>
              <th className="px-2">Effect</th>
            </tr>
          </thead>
          <tbody>
            {t.events.map((e) => (
              <tr key={e.beforeRound} className="border-t border-border">
                <td className="py-1.5 pr-3 tabular-nums text-text">R{e.beforeRound}</td>
                <td className="px-2 tabular-nums text-text-faint">{e.gapSec}s</td>
                <td className="px-2 text-text-dim">
                  {e.streakTeam ? `${e.streakTeam} on ${e.streakLen} straight` : 'no streak'}
                </td>
                <td className="px-2" style={{ color: e.calledBy === ourTeam ? 'var(--accent)' : 'var(--text-dim)' }}>
                  {e.calledBy ?? '—'}
                  {e.calledBy && (
                    <span className="ml-1 text-[9px] text-text-faint" title={e.callSource === 'chat' ? 'confirmed by a !tac/.tac in chat' : 'inferred — no chat command (called from the in-game menu)'}>
                      {e.callSource === 'chat' ? '✓' : '~'}
                    </span>
                  )}
                </td>
                <td className="px-2 text-text-dim">{e.nextRoundWinner ?? '—'}</td>
                <td className="px-2">
                  {e.streakLen >= 2 ? (
                    <span style={{ color: e.brokeStreak ? 'var(--win)' : 'var(--loss)' }}>
                      {e.brokeStreak ? 'broke the run' : 'run continued'}
                    </span>
                  ) : (
                    <span className="text-text-faint">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] leading-relaxed text-text-faint">
        Timeouts aren’t recorded in the demo. They’re detected from the break before a round —
        normally {t.baselineGapSec}s here, and a timeout adds {t.timeoutSec}s (halftime side-swaps are
        excluded). “Called by” is taken from chat when a <code>!tac</code>/<code>.tac</code>/<code>!pause</code> was
        typed (<span className="text-text-dim">✓ confirmed</span>, coaches included) — every command in this match
        landed inside a detected break, which independently confirms the detection. Timeouts called from the in-game
        menu leave no command, so those are <span className="text-text-dim">~ inferred</span> from who was being run over.
      </p>
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className="font-display tabular-nums text-text">{value}</div>
      {sub && <div className="text-[10px] text-text-dim">{sub}</div>}
    </div>
  )
}

// ---------- Heatmap (density) ----------
const HSIZE = 480
let _pal: Uint8ClampedArray | null = null
function palette(): Uint8ClampedArray {
  if (_pal) return _pal
  const c = document.createElement('canvas')
  c.width = 256
  c.height = 1
  const g = c.getContext('2d')!
  const grd = g.createLinearGradient(0, 0, 256, 0)
  grd.addColorStop(0.0, 'rgba(20,40,90,0)')
  grd.addColorStop(0.25, 'rgba(60,120,220,0.5)')
  grd.addColorStop(0.5, 'rgba(90,205,190,0.8)')
  grd.addColorStop(0.75, 'rgba(232,200,90,0.92)')
  grd.addColorStop(1.0, 'rgba(222,90,90,1)')
  g.fillStyle = grd
  g.fillRect(0, 0, 256, 1)
  _pal = g.getImageData(0, 0, 256, 1).data
  return _pal
}

function Heatmap({ deep, ourTeam, rounds }: { deep: DeepStats; ourTeam: string; rounds: MatchData['round_timeline'] }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [which, setWhich] = useState<'all' | 'jano' | 'opp'>('all')
  // Nuke/vertigo have a lower level with its own radar; one image can't show both.
  const [layer, setLayer] = useState<'upper' | 'lower'>('upper')
  // Which side WE were on each round — the winning team plus the winning side gives both.
  // "We die a lot here" and "our CT setup gets run over" are different problems.
  const [ourSide, setOurSide] = useState<'all' | 'CT' | 'T'>('all')
  const layered = deep.events.some((e) => e.layer)
  // Whether the round timeline told us which side we were on. The side filter is meaningless
  // without it, and this is the only part of that map the buttons need.
  const knowsSides = (rounds ?? []).some((r) => r.winner_team && r.winner_side)

  // The points to draw, and the number under the map — one computation, so they cannot disagree.
  //
  // This filter used to be a plain function called from both the paint effect and the caption, and
  // the effect's dependency list left out `ourSide`. Switching CT/T changed the caption and NOT the
  // canvas: the map kept showing every side while the number under it said otherwise. The lint rule
  // had been silenced with a disable comment, which is how it survived. Deriving the list here means
  // the dependency is the data, not a list somebody has to keep in sync.
  const pts = useMemo(() => {
    const sideOf = new Map<number, string>()
    for (const r of rounds ?? []) {
      if (!r.winner_team || !r.winner_side) continue
      sideOf.set(r.round, r.winner_team === ourTeam ? r.winner_side : r.winner_side === 'CT' ? 'T' : 'CT')
    }
    return deep.events.filter(
      (e) =>
        (which === 'all' ? true : which === 'jano' ? e.atkTeam === ourTeam : e.atkTeam !== ourTeam) &&
        (!layered || (e.layer ?? 'upper') === layer) &&
        (ourSide === 'all' || sideOf.get(e.round) === ourSide),
    )
  }, [deep, rounds, ourTeam, which, layer, layered, ourSide])

  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    const ctx = cv.getContext('2d')!
    const t = transformFor(deep.map)
    const size = 1024
    // Guard: a fast filter/floor toggle can start a new load before the old radar resolves, and a
    // late onload would repaint the previous filter over the current one. RadarCanvas does this;
    // three call sites copied the pattern without the flag.
    let dead = false
    const radar = new Image()
    const paint = () => {
      if (dead) return
      ctx.clearRect(0, 0, HSIZE, HSIZE)
      if (radar.complete && radar.naturalWidth) ctx.drawImage(radar, 0, 0, HSIZE, HSIZE)
      ctx.fillStyle = 'rgba(9,12,19,0.64)' // dim the map so hotspots read
      ctx.fillRect(0, 0, HSIZE, HSIZE)
      // accumulate intensity on an offscreen canvas
      const heat = document.createElement('canvas')
      heat.width = heat.height = HSIZE
      const h = heat.getContext('2d')!
      const RAD = 26
      for (const e of pts) {
        const cx = ((e.x - t.posX) / t.scale / size) * HSIZE
        const cy = ((t.posY - e.y) / t.scale / size) * HSIZE
        const g = h.createRadialGradient(cx, cy, 0, cx, cy, RAD)
        g.addColorStop(0, 'rgba(0,0,0,0.3)')
        g.addColorStop(1, 'rgba(0,0,0,0)')
        h.fillStyle = g
        h.fillRect(cx - RAD, cy - RAD, RAD * 2, RAD * 2)
      }
      // colorize by accumulated alpha
      const img = h.getImageData(0, 0, HSIZE, HSIZE)
      const d = img.data
      const pal = palette()
      for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3]
        if (!a) continue
        const j = a * 4
        d[i] = pal[j]
        d[i + 1] = pal[j + 1]
        d[i + 2] = pal[j + 2]
        d[i + 3] = pal[j + 3]
      }
      h.putImageData(img, 0, 0)
      ctx.drawImage(heat, 0, 0)
    }
    radar.onload = paint
    radar.src = `/api/radar/${layered && layer === 'lower' ? `${deep.map}_lower` : deep.map}?v=cs2`
    paint()
    return () => {
      dead = true
    }
  }, [deep, pts, layer, layered])

  const opts: { k: 'all' | 'jano' | 'opp'; label: string }[] = [
    { k: 'all', label: 'All kills' },
    { k: 'jano', label: `${ourTeam} kills` },
    { k: 'opp', label: 'Where we die' },
  ]
  // Counts what is DRAWN — the same array the canvas paints, so the two can't drift.
  const count = pts.length

  return (
    <>
      <div className="flex items-center justify-between">
        <SectionLabel n="H" label="Kill heatmap" />
        <div className="flex gap-1">
          {opts.map((o) => (
            <button
              key={o.k}
              onClick={() => setWhich(o.k)}
              className="px-2.5 py-1 text-xs"
              style={{
                borderRadius: 'var(--radius)',
                color: which === o.k ? 'var(--accent-ink)' : 'var(--text-dim)',
                background: which === o.k ? 'var(--accent)' : 'transparent',
                border: '1px solid var(--border)',
              }}
            >
              {o.label}
            </button>
          ))}
          {knowsSides && (
            <>
              <span className="mx-1 self-center text-text-faint">|</span>
              {(['all', 'CT', 'T'] as const).map((sd) => (
                <button
                  key={sd}
                  onClick={() => setOurSide(sd)}
                  title={sd === 'all' ? 'Both sides' : `Rounds we played ${sd}`}
                  className="px-2.5 py-1 text-xs"
                  style={{
                    borderRadius: 'var(--radius)',
                    color: ourSide === sd ? 'var(--text)' : 'var(--text-dim)',
                    background: ourSide === sd ? 'var(--surface-2)' : 'transparent',
                    border: '1px solid var(--border)',
                  }}
                >
                  {sd === 'all' ? 'both' : `our ${sd}`}
                </button>
              ))}
            </>
          )}
          {layered && (
            <>
              <span className="mx-1 self-center text-text-faint">|</span>
              {(['upper', 'lower'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLayer(l)}
                  title="This map has two levels, each with its own radar"
                  className="px-2.5 py-1 text-xs"
                  style={{
                    borderRadius: 'var(--radius)',
                    color: layer === l ? 'var(--accent-ink)' : 'var(--text-dim)',
                    background: layer === l ? 'var(--accent)' : 'transparent',
                    border: '1px solid var(--border)',
                  }}
                >
                  {l}
                </button>
              ))}
            </>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-3">
        <canvas
          ref={ref}
          width={HSIZE}
          height={HSIZE}
          className="w-full border border-border"
          style={{ borderRadius: 'var(--radius)', maxWidth: HSIZE, background: 'var(--bg-2)' }}
        />
        <div className="flex items-center gap-3 text-[11px] text-text-dim">
          <span>fewer</span>
          <span
            className="inline-block h-2 w-28"
            style={{ borderRadius: '2px', background: 'linear-gradient(90deg,#3c78dc,#5acdbe,#e8c85a,#de5a5a)' }}
          />
          <span>more</span>
          <span className="text-text-faint">· {count} kills</span>
        </div>
      </div>
    </>
  )
}
